/**
 * MessageBadge — chat icon with unread count for the sidebar and mobile header.
 * For assistants it opens a new-message dialog pre-filled with the current page.
 * For admins it navigates to /communicate.
 */
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { MessageSquare } from "lucide-react";
import { useLocation } from "wouter";

interface MessageBadgeProps {
  variant?: "sidebar" | "mobile";
}

export function MessageBadge({ variant = "sidebar" }: MessageBadgeProps) {
  const [, navigate] = useLocation();

  const { data } = useQuery<{ count: number }>({
    queryKey: ["/api/communicate/unread-count"],
    queryFn: async () => {
      const res = await fetch("/api/communicate/unread-count");
      if (!res.ok) return { count: 0 };
      return res.json();
    },
    refetchInterval: 15000,
    staleTime: 10000,
  });

  const count = data?.count ?? 0;

  if (variant === "mobile") {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="relative w-8 h-8"
        onClick={() => navigate("/communicate")}
        data-testid="button-mobile-messages"
        aria-label="Messages"
      >
        <MessageSquare className="w-4 h-4" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </Button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative w-8 h-8"
          onClick={() => navigate("/communicate")}
          data-testid="button-sidebar-messages"
          aria-label="Messages"
        >
          <MessageSquare className="w-4 h-4" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">
        Messages{count > 0 ? ` (${count} unread)` : ""}
      </TooltipContent>
    </Tooltip>
  );
}
